import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRealtime, type RealtimeEnvelope } from '../services/realtime'
import { useTickerStore } from '../stores/ticker'
import { useLiveStore, type LiveTrigger } from '../stores/live'
import { useWaveStore } from '../stores/wave'
import { useUiStore } from '../stores/ui'
import { toAlertLevel, eventTypeLabel } from '../lib/alert-level'

function topicParts(topic: string): string[] {
  return topic.split('/')
}

const num = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** MQTT → TanStack Query cache 桥 + live store 直写。AppShell 全局挂载一次。 */
export function useRealtimeBridge() {
  const qc = useQueryClient()
  const debounceRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const debouncedInvalidate = useCallback(
    (key: string, queryKey: readonly string[]) => {
      const timers = debounceRef.current
      const existing = timers.get(key)
      if (existing) clearTimeout(existing)
      timers.set(
        key,
        setTimeout(() => {
          timers.delete(key)
          void qc.invalidateQueries({ queryKey })
        }, 2000),
      )
    },
    [qc],
  )

  const handleMessage = useCallback(
    (topic: string, msg: RealtimeEnvelope) => {
      const parts = topicParts(topic)

      if (parts[0] === 'alert') {
        const level = toAlertLevel(typeof msg.level === 'string' ? msg.level : '')
        const kind = typeof msg.type === 'string' ? msg.type : 'earthquake'
        const name = typeof msg.epicenter_name === 'string' ? msg.epicenter_name : '未知区域'
        const mag = typeof msg.magnitude === 'number' ? msg.magnitude : undefined
        useTickerStore.getState().push({
          key: msg.message_id ?? `${topic}-${Date.now()}`,
          eventId: msg.event_id,
          title: `${eventTypeLabel(kind)} · ${name}${mag ? ` M${mag.toFixed(1)}` : ''}`,
          level,
          ts: Date.now(),
        })
        void qc.invalidateQueries({ queryKey: ['earthquakes'] })
        void qc.invalidateQueries({ queryKey: ['stats'] })
        void qc.invalidateQueries({ queryKey: ['events'] })

        // 启动 P/S 波传播环：预警到达即演（GlobalQuake 式实时观感）
        const lat = num(msg.epicenter_lat)
        const lon = num(msg.epicenter_lon)
        if (lat !== 0 || lon !== 0) {
          let originTs = num(msg.timestamp)
          // timestamp 缺失或明显偏离当前时间时退化为收到时刻，避免环直接跳到终点
          if (originTs <= 0 || Math.abs(Date.now() - originTs) > 30 * 60_000) originTs = Date.now()
          useWaveStore.getState().startWave({
            eventId: typeof msg.event_id === 'string' ? msg.event_id : '',
            lon,
            lat,
            originTs,
            magnitude: num(msg.magnitude),
          })
          useUiStore.getState().focusMap(lat, lon, 7)
        }
        return
      }

      if (parts[0] === 'earthquake') {
        void qc.invalidateQueries({ queryKey: ['earthquakes'] })
        void qc.invalidateQueries({ queryKey: ['stats'] })
        return
      }

      if (parts[0] === 'event' && parts[2] === 'update') {
        void qc.invalidateQueries({ queryKey: ['events'] })
        void qc.invalidateQueries({ queryKey: ['incidents'] })
        void qc.invalidateQueries({ queryKey: ['reports'] })
        return
      }

      if (parts[0] === 'trigger') {
        // 触发即时上屏：直写 live store 供地图脉冲/站点面板消费（不再等防抖回查）
        // 注意：models.TriggerReport 的 JSON tag 为 camelCase（triggerId/deviceId/staLtaRatio/createdAt）
        const lat = num(msg.lat)
        const lon = num(msg.lon)
        if (lat !== 0 || lon !== 0) {
          const deviceId = typeof msg.deviceId === 'string' ? msg.deviceId : 'unknown'
          const t: LiveTrigger = {
            triggerId: typeof msg.triggerId === 'string' ? msg.triggerId : `${deviceId}-${Date.now()}`,
            deviceId,
            lat,
            lon,
            iqr: num(msg.iqr),
            cav: num(msg.cav),
            staLtaRatio: num(msg.staLtaRatio),
            confirmed: msg.confirmed === true,
            ts: num(msg.createdAt) || Date.now(),
          }
          useLiveStore.getState().pushTrigger(t)
        }
        debouncedInvalidate('triggers', ['triggers'])
        void qc.invalidateQueries({ queryKey: ['stats'] })
        return
      }

      if (parts[0] === 'device') {
        const lat = num(msg.lat)
        const lon = num(msg.lon)
        if (typeof msg.device_id === 'string' && (lat !== 0 || lon !== 0)) {
          useLiveStore.getState().pushDevice({
            deviceId: msg.device_id,
            lat,
            lon,
            battery: msg.battery == null ? undefined : num(msg.battery),
            ts: num(msg.timestamp) || Date.now(),
          })
        }
        debouncedInvalidate('devices', ['devices'])
        return
      }
    },
    [qc, debouncedInvalidate],
  )

  useRealtime(handleMessage)

  useEffect(() => () => {
    debounceRef.current.forEach((t) => clearTimeout(t))
    debounceRef.current.clear()
  }, [])
}
